import { useEffect, useState } from 'react';
import { Banner } from '@shopify/polaris';
import { useApi } from '../providers/ApiProvider';
import { t } from '../i18n';

/**
 * Same product-block status check as Milestone progress:
 * probe the live product template + theme-editor deep link.
 */
export function ProductBlockStatusBanner({
  block = 'product-promo',
  blockName = 'Freebi Product promotion',
}: {
  block?: string;
  blockName?: string;
}) {
  const api = useApi();
  const [state, setState] = useState<{
    loaded: boolean;
    active: boolean;
    deepLink: string;
  }>({ loaded: false, active: false, deepLink: '' });

  useEffect(() => {
    let cancelled = false;
    api
      .get<{ active?: boolean; deepLink?: string }>(
        `/stores/theme/product-block/${encodeURIComponent(block)}`,
      )
      .then((r) => {
        if (cancelled) return;
        setState({
          loaded: true,
          active: Boolean(r.active),
          deepLink: r.deepLink || '',
        });
      })
      .catch(() => {
        if (cancelled) return;
        setState({ loaded: true, active: false, deepLink: '' });
      });
    return () => {
      cancelled = true;
    };
  }, [api, block]);

  if (!state.loaded) return null;

  if (state.active) {
    return (
      <Banner
        tone="success"
        title={t('{{name}} is on your product page', { name: t(blockName) })}
      >
        <p>
          {t("The product block is active. Campaigns that enable this widget will\n          show on the product page.")}
        </p>
      </Banner>
    );
  }

  const canOpen = Boolean(state.deepLink && state.deepLink !== '#');
  return (
    <Banner
      tone="warning"
      title={t("Add block under Buy (recommended)")}
      action={
        canOpen
          ? {
              content: t("Open theme editor"),
              onAction: () => {
                window.open(state.deepLink, '_blank', 'noopener,noreferrer');
              },
            }
          : undefined
      }
    >
      <p>
        {t("Opens the product template with")} <strong>{blockName}</strong> {t("ready to\n        add. Place it directly under the Buy buttons, then save the theme.")}
      </p>
    </Banner>
  );
}
